/**
 * The signed-in user's own profile: name, company, role, phone and avatar.
 *
 * Administration of *other* accounts is the Users page (UserAdmin.js); this
 * page only ever edits the caller's own row.
 */
import React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  TextField,
  Stack,
  Button,
  Typography,
  Box,
  Paper,
  Snackbar,
  Alert,
  InputAdornment,
  Avatar,
  Divider,
} from "@mui/material";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import {
  formatPhoneForDb,
  parsePhoneFromDb,
  getPhoneCountryOptions,
} from "../utils/phoneProfile";
import ProfilePhoneComboField from "../components/ProfilePhoneComboField";
import { flattenDjangoRestError, copy as userCopy } from "../utils/userFacingMessages";

function Profile() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("DE");
  const [phoneNational, setPhoneNational] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [role, setRole] = useState("");
  const [profileReady, setProfileReady] = useState(false);
  const [saveChanges,setSaveChanges] = useState(true);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [toastMessage, setToastMessage] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const navigate = useNavigate();
  const authConfig = () => ({
    headers: {
      Authorization: "Token " + (localStorage.getItem("token") || ""),
    },
  });

  const showToast = useCallback((message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  }, []);

  const countryOptions = useMemo(() => getPhoneCountryOptions(), []);

  const loadAvatar = useCallback(async () => {
    try {
      const res = await axios.get("api/user/avatar", {
        ...authConfig(),
        responseType: "blob",
      });
      setAvatarUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch (err) {
      // 404 = no image yet; the letter fallback is shown.
      setAvatarUrl(null);
    }
  }, []);

  useEffect(() => {
    loadAvatar();
  }, [loadAvatar]);

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setAvatarBusy(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      await axios.post("api/user/avatar", formData, {
        headers: {
          ...authConfig().headers,
          "Content-Type": "multipart/form-data",
        },
      });
      await loadAvatar();
      // Tell the side menu to refresh its avatar too.
      window.dispatchEvent(new Event("avatar-updated"));
      showToast("Profile image updated.", "success");
    } catch (err) {
      console.error("Avatar upload failed", err);
      showToast(
        err?.response?.data?.error || "Could not upload the profile image.",
        "error"
      );
    } finally {
      setAvatarBusy(false);
    }
  };

  const emailLooksValid = useMemo(() => {
    const t = (email || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
  }, [email]);

  const validAdornment = useMemo(
    () => (
      <InputAdornment position="end">
        <CheckCircleIcon sx={{ color: "success.main", fontSize: 22 }} aria-hidden />
      </InputAdornment>
    ),
    []
  );

  useEffect(() => {
    let cancelled = false;
    const cfg = authConfig();

    (async () => {
      try {
        const res = await axios.get("api/user/profile/", cfg);
        if (cancelled || res.status !== 200 || !res.data) {
          return;
        }

        const u = res.data;
        const id = u.id != null ? String(u.id) : "";
        if (id) {
          localStorage.setItem("userId", id);
        }
        if (!cancelled) {
          setFirstName(u.first_name ?? "");
          setLastName(u.last_name ?? "");
          setEmail(u.username ?? "");
          setCompanyName(u.company_name ?? "");
          const parsedPhone = parsePhoneFromDb(u.phone_number ?? "", "DE");
          setPhoneCountry(parsedPhone.country);
          setPhoneNational(parsedPhone.national);
          const rawRole = u.role ?? "";
          setRole(rawRole === "not_specified" ? "" : rawRole);
          setProfileReady(true);
          setSaveChanges(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Profile load failed", err);
          if (err?.response?.status === 401) {
            localStorage.removeItem("token");
            localStorage.removeItem("userId");
            showToast(userCopy.profileLoadSession, "error");
            navigate("/login");
          } else {
            const data = err?.response?.data;
            const fromApi = flattenDjangoRestError(data);
            showToast(fromApi || userCopy.profileLoadFailed, "error");
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, showToast]);

  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
    setSaveChanges(false)
  };
  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
    setSaveChanges(false)

  }
  const handleCompanyNameChange = (event) => {
    setCompanyName(event.target.value);
    setSaveChanges(false)

  }
  const handleRoleChange = (event) => {
    setRole(event.target.value);
    setSaveChanges(false);
  };
  const handlePhoneCountryChange = (code) => {
    setPhoneCountry(code || "DE");
    setSaveChanges(false);
  };
  const handlePhoneNationalDigitsChange = (digits) => {
    setPhoneNational(digits);
    setSaveChanges(false);
  };
  const handleChangePassword = ()=> {
    navigate("/passwordchange");
  }
  const handleCloseToast = (_, reason) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  };
  let handleSubmit = async (e) => {
    e.preventDefault();
    if (!profileReady) {
      showToast(userCopy.profileStillLoading, "error");
      return;
    }
    const phone_number = formatPhoneForDb(phoneCountry, phoneNational);
    if (!phone_number) {
      showToast(userCopy.profilePhoneRequired, "error");
      return;
    }
    const data = {
      first_name: firstName,
      last_name: lastName,
      company_name: companyName,
      phone_number,
      role,
    };
    try {
      const res = await axios.patch("api/user/profile/", data, authConfig());
      if (res.status === 200) {
        setSaveChanges(true);
        showToast(userCopy.profileSaved, "success");
        // The side menu shows the first name.
        window.dispatchEvent(new Event("profile-updated"));
      } else {
        showToast(userCopy.profileSaveFailedGeneric, "error");
      }
    } catch (err) {
      const status = err?.response?.status;
      const raw = err?.response?.data;
      console.error("Profile update failed", status, raw);
      let msg = flattenDjangoRestError(raw);
      if (!msg && status === 500) {
        msg = userCopy.profileSaveServer;
      }
      if (!msg && !err.response) {
        msg = userCopy.profileSaveNetwork;
      }
      if (!msg) {
        msg = userCopy.profileSaveFallback;
      }
      showToast(msg, "error");
    }
  }
  return (
    <Stack
      direction='column'
      justifyContent='center'
      alignItems='center'
      spacing={4}
      marginTop='2%'
    >
      <Box sx={{ width: "100%", maxWidth: "1150px", px: 2, boxSizing: "border-box" }}>
        <Stack
          sx={{
            width: "100%",
            py: 2,
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f6f8 0%, #ecedf1 100%)",
            border: "1px solid #dde1e7",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ px: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your account information.
            </Typography>
            {!profileReady && (
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                Loading your profile…
              </Typography>
            )}
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            mt: 4,
            mb: 4,
            borderRadius: 3,
            border: "1px solid #e7ebf3",
            backgroundColor: "#ffffff",
            p: { xs: 2, md: 3 },
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Avatar
                src={avatarUrl || undefined}
                sx={{
                  width: 88,
                  height: 88,
                  bgcolor: "rgba(76, 126, 255, 0.15)",
                  color: "primary.main",
                  fontWeight: 700,
                  fontSize: "2rem",
                }}
              >
                {(firstName || email || "?").charAt(0).toUpperCase()}
              </Avatar>
              <Stack spacing={0.75}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  disabled={avatarBusy}
                  startIcon={<PhotoCameraOutlinedIcon />}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, alignSelf: "flex-start" }}
                >
                  {avatarBusy ? "Uploading…" : avatarUrl ? "Change photo" : "Upload photo"}
                  <input
                    hidden
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={handleAvatarUpload}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  JPG, PNG or WEBP, max 5 MB. Shown in the side menu.
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                id="firstname"
                label="First name"
                value={firstName}
                onChange={handleFirstNameChange}
                type='text'
                fullWidth
                InputProps={{
                  endAdornment:
                    profileReady && firstName.trim().length > 0 ? validAdornment : null,
                }}
              />
              <TextField
                id="lastname"
                label="Last name"
                value={lastName}
                onChange={handleLastNameChange}
                type='text'
                fullWidth
                InputProps={{
                  endAdornment:
                    profileReady && lastName.trim().length > 0 ? validAdornment : null,
                }}
              />
            </Stack>

            <TextField
              id="email"
              label="Email"
              value={email}
              type='email'
              fullWidth
              disabled
              InputProps={{
                endAdornment: profileReady && emailLooksValid ? validAdornment : null,
              }}
            />
            <TextField
              id="companyname"
              label="Company name"
              value={companyName}
              onChange={handleCompanyNameChange}
              type='text'
              fullWidth
              InputProps={{
                endAdornment:
                  profileReady && companyName.trim().length > 0 ? validAdornment : null,
              }}
            />
            <TextField
              id="profile-role"
              label="Job Title"
              value={role}
              onChange={handleRoleChange}
              type='text'
              fullWidth
              placeholder="e.g. Process engineer"
              InputProps={{
                endAdornment: profileReady ? validAdornment : null,
              }}
            />
            <ProfilePhoneComboField
              profileReady={profileReady}
              countryCode={phoneCountry}
              nationalDigits={phoneNational}
              options={countryOptions}
              onCountryChange={handlePhoneCountryChange}
              onNationalDigitsChange={handlePhoneNationalDigitsChange}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="outlined"
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                onClick={handleChangePassword}
              >
                Change password
              </Button>
              <Button
                disabled={!profileReady || saveChanges}
                variant="contained"
                sx={{
                  height: "48px",
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                }}
                onClick={handleSubmit}
              >
                Save Changes
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3500}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseToast} severity={toastSeverity} sx={{ width: "100%" }} variant="filled">
          {toastMessage}
        </Alert>
      </Snackbar>
    </Stack>

  );
}

export default Profile;
